import type { ListBuilderConfiguration } from '../types';

/**
 * Voice category value mapping
 */
const VOICE_CATEGORY_VALUE_MAPPING = {
	cloned: { bg: '#8b5cf6', text: '#ffffff' },
	premade: { bg: '#3b82f6', text: '#ffffff' },
	generated: { bg: '#10b981', text: '#ffffff' },
	professional: { bg: '#f59e0b', text: '#ffffff' },
};

/**
 * Gender value mapping
 */
const GENDER_VALUE_MAPPING = {
	male: { bg: '#60a5fa', text: '#ffffff' },
	female: { bg: '#f472b6', text: '#ffffff' },
	neutral: { bg: '#6b7280', text: '#ffffff' },
};

/**
 * Favorite star value mapping
 */
const FAVORITE_VALUE_MAPPING = {
	0: { icon: 'far fa-star', color: '#9ca3af' },
	1: { icon: 'fas fa-star', color: '#fbbf24' },
};

/**
 * Conversation Voice Profile list configuration
 * Shows available voice profiles in the system
 * Converted from JSON to TypeScript for type safety
 */
export const CONVERSATION_VOICE_PROFILE_CONFIG: ListBuilderConfiguration = {
	lists: [
		{
			id: 'default-voices-all',
			cacheTTL: 600000,
			name: 'All Voices',
			type: 'default',
			active: true,
			sortOrder: 0,
			columns: [
				{
					id: 'audio-preview',
					width: 60,
					minWidth: 60,
					maxWidth: 60,
					fields: [
						{
							id: 'audio-player-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: '_id',
									objectTable: 'conversation_voice_profile',
								},
							},
							styleConfig: {
								size: 'md',
								colorRule: { type: 'default' },
							},
							displayStyle: 'audio_player',
						},
					],
					headers: [{ id: 'h-audio', text: '' }],
					sortable: false,
					alignment: 'center',
					verticalAlignment: 'middle',
				},
				{
					id: 'voice-info',
					fields: [
						{
							id: 'favorite-star',
							action: {
								type: 'toggle_favorite',
								endpoint: '/api/ai-agents/voices/:id/favorite',
							},
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'is_favorite',
									objectTable: 'conversation_voice_profile',
								},
							},
							styleConfig: {
								size: 'sm',
								colorRule: {
									type: 'value-mapping',
									valueMapping: FAVORITE_VALUE_MAPPING,
								},
							},
							displayStyle: 'icon',
						},
						{
							id: 'voice-name',
							link: {
								type: 'object',
								enabled: true,
								objectTable: 'conversation_voice_profile',
							},
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'name',
									objectTable: 'conversation_voice_profile',
								},
							},
							styleConfig: {
								size: 'md',
								colorRule: { type: 'default' },
								textExpansion: { enabled: false },
							},
							displayStyle: 'text',
						},
						{
							id: 'line-break-1',
							dataConfig: { type: 'custom_text', customText: '' },
							styleConfig: { size: 'md', colorRule: { type: 'default' } },
							displayStyle: 'line_break',
						},
						{
							id: 'description',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'description',
									objectTable: 'conversation_voice_profile',
								},
							},
							styleConfig: {
								size: 'xs',
								ellipsis: true,
								colorRule: {
									type: 'static',
									staticColor: { text: '#6b7280' },
								},
								maxLength: 100,
							},
							displayStyle: 'text',
						},
					],
					headers: [{ id: 'h1', text: 'Voice' }],
					sortable: true,
					alignment: 'left',
					sortFields: [
						{
							direction: 'ASC',
							fieldName: 'name',
							objectTable: 'conversation_voice_profile',
						},
					],
					verticalAlignment: 'top',
				},
				{
					id: 'voice-characteristics',
					fields: [
						{
							id: 'voice-category',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'category',
									objectTable: 'conversation_voice_profile',
								},
							},
							styleConfig: {
								size: 'xs',
								colorRule: {
									type: 'value-mapping',
									valueMapping: VOICE_CATEGORY_VALUE_MAPPING,
								},
							},
							displayStyle: 'badge',
						},
						{
							id: 'spacing-category',
							dataConfig: { type: 'custom_text', customText: ' ' },
							styleConfig: { size: 'xs', colorRule: { type: 'default' } },
							displayStyle: 'text',
						},
						{
							id: 'use-case',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'use_case',
									objectTable: 'conversation_voice_profile',
								},
							},
							styleConfig: {
								size: 'xs',
								colorRule: {
									type: 'static',
									staticColor: { bg: '#fbbf24', text: '#78350f' },
								},
							},
							displayStyle: 'badge',
						},
						{
							id: 'line-break-2',
							dataConfig: { type: 'custom_text', customText: '' },
							styleConfig: { size: 'md', colorRule: { type: 'default' } },
							displayStyle: 'line_break',
						},
						{
							id: 'gender',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'gender',
									objectTable: 'conversation_voice_profile',
								},
							},
							styleConfig: {
								size: 'xs',
								colorRule: {
									type: 'value-mapping',
									valueMapping: GENDER_VALUE_MAPPING,
								},
							},
							displayStyle: 'badge',
						},
						{
							id: 'spacing-2',
							dataConfig: { type: 'custom_text', customText: ' ' },
							styleConfig: { size: 'xs', colorRule: { type: 'default' } },
							displayStyle: 'text',
						},
						{
							id: 'age-range',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'age_range',
									objectTable: 'conversation_voice_profile',
								},
							},
							styleConfig: {
								size: 'xs',
								colorRule: {
									type: 'static',
									staticColor: { bg: '#e5e7eb', text: '#374151' },
								},
							},
							displayStyle: 'badge',
						},
					],
					headers: [{ id: 'h2', text: 'Characteristics' }],
					sortable: false,
					alignment: 'left',
					verticalAlignment: 'top',
				},
				{
					id: 'voice-language-accent',
					fields: [
						{
							id: 'language',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'language',
									objectTable: 'conversation_voice_profile',
								},
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'default' },
							},
							displayStyle: 'text',
						},
						{
							id: 'line-break-lang',
							dataConfig: { type: 'custom_text', customText: '' },
							styleConfig: { size: 'md', colorRule: { type: 'default' } },
							displayStyle: 'line_break',
						},
						{
							id: 'accent',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'accent',
									objectTable: 'conversation_voice_profile',
								},
							},
							styleConfig: {
								size: 'xs',
								colorRule: {
									type: 'static',
									staticColor: { text: '#6b7280' },
								},
							},
							displayStyle: 'text',
						},
					],
					headers: [{ id: 'h3', text: 'Language & Accent' }],
					sortable: false,
					alignment: 'left',
					verticalAlignment: 'top',
				},
				{
					id: 'voice-popularity',
					fields: [
						{
							id: 'likes-count',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'liked_by_count',
									objectTable: 'conversation_voice_profile',
								},
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'default' },
							},
							displayStyle: 'text',
						},
						{
							id: 'likes-label',
							dataConfig: { type: 'custom_text', customText: ' likes' },
							styleConfig: {
								size: 'xs',
								colorRule: {
									type: 'static',
									staticColor: { text: '#6b7280' },
								},
							},
							displayStyle: 'text',
						},
						{
							id: 'line-break-3',
							dataConfig: { type: 'custom_text', customText: '' },
							styleConfig: { size: 'md', colorRule: { type: 'default' } },
							displayStyle: 'line_break',
						},
						{
							id: 'clones-count',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'cloned_by_count',
									objectTable: 'conversation_voice_profile',
								},
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'default' },
							},
							displayStyle: 'text',
						},
						{
							id: 'clones-label',
							dataConfig: { type: 'custom_text', customText: ' clones' },
							styleConfig: {
								size: 'xs',
								colorRule: {
									type: 'static',
									staticColor: { text: '#6b7280' },
								},
							},
							displayStyle: 'text',
						},
					],
					headers: [{ id: 'h4', text: 'Popularity' }],
					sortable: true,
					alignment: 'center',
					sortFields: [
						{
							direction: 'DESC',
							fieldName: 'liked_by_count',
							objectTable: 'conversation_voice_profile',
						},
					],
					verticalAlignment: 'top',
				},
			],
			description: 'All available voice profiles in the system',
			defaultSortId: 'column-voice-info-asc',
		},
	],
	paging: {
		defaultPageSize: 25,
		pageSizeOptions: [25, 50, 100],
	},
	filters: {
		activeFilters: [
			'voiceSearch',
			'voiceCategory',
			'useCase',
			'gender',
			'voiceLanguages',
			'accent',
			'ageRange',
			'isFavorite',
		],
		defaultFilters: {},
	},
	objects: [
		{
			baseUrl: '/ai-agents/voices/:id',
			objectLabel: 'Voice Profile',
			objectTable: 'conversation_voice_profile',
			selectedFields: [
				{ fieldName: '_id', fieldLabel: 'ID' },
				{ fieldName: 'name', fieldLabel: 'Name' },
				{ fieldName: 'description', fieldLabel: 'Description' },
				{ fieldName: 'preview_url', fieldLabel: 'Preview URL' },
				{ fieldName: 'category', fieldLabel: 'Category' },
				{ fieldName: 'use_case', fieldLabel: 'Use Case' },
				{ fieldName: 'gender', fieldLabel: 'Gender' },
				{ fieldName: 'language', fieldLabel: 'Language' },
				{ fieldName: 'accent', fieldLabel: 'Accent' },
				{ fieldName: 'age_range', fieldLabel: 'Age Range' },
				{ fieldName: 'is_favorite', fieldLabel: 'Favorite' },
				{ fieldName: 'liked_by_count', fieldLabel: 'Likes' },
				{ fieldName: 'cloned_by_count', fieldLabel: 'Clones' },
				{ fieldName: '_created_date', fieldLabel: 'Created' },
			],
		},
	],
	tableSorts: [
		{
			id: 'name',
			name: 'Name',
			fields: [
				{
					direction: 'ASC',
					fieldName: 'name',
					objectTable: 'conversation_voice_profile',
				},
			],
			description: 'Sort voices by name',
		},
		{
			id: 'favorites',
			name: 'Favorites First',
			fields: [
				{
					direction: 'DESC',
					fieldName: 'is_favorite',
					objectTable: 'conversation_voice_profile',
				},
				{
					direction: 'ASC',
					fieldName: 'name',
					objectTable: 'conversation_voice_profile',
				},
			],
			description: 'Show favorite voices first',
		},
		{
			id: 'most-liked',
			name: 'Most Liked',
			fields: [
				{
					direction: 'DESC',
					fieldName: 'liked_by_count',
					objectTable: 'conversation_voice_profile',
				},
			],
			description: 'Sort by likes from ElevenLabs',
		},
		{
			id: 'most-cloned',
			name: 'Most Cloned',
			fields: [
				{
					direction: 'DESC',
					fieldName: 'cloned_by_count',
					objectTable: 'conversation_voice_profile',
				},
			],
			description: 'Sort by clones from ElevenLabs',
		},
		{
			id: 'created',
			name: 'Recently Added',
			fields: [
				{
					direction: 'DESC',
					fieldName: '_created_date',
					objectTable: 'conversation_voice_profile',
				},
			],
			description: 'Sort by creation date',
		},
	],
};
